import { useState, useEffect } from 'react';
// import { useNavigate } from 'react-router-dom'; // Reserved for future navigation
import { Users, Briefcase, Award, Globe, Target } from 'lucide-react';
import { careerService } from '../services/apiService';

const CareerForm = ({ career, onClose, onSave }) => {
  // const navigate = useNavigate(); // Reserved for future navigation
  const [formData, setFormData] = useState({
    title: '',
    category: 'Entry Level',
    positions: '1',
    background: '',
    experience: '',
    salary: '',
    duration: '',
    skills: '',
    responsibilities: '',
    location: 'Bhugaon, Pune',
    isRemote: false,
    isActive: true
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (career) {
      setFormData({
        title: career.title || '',
        category: career.category || 'Entry Level',
        positions: career.positions?.toString() || '1',
        background: career.background || '',
        experience: career.experience || '',
        salary: career.salary || '',
        duration: career.duration || '',
        skills: Array.isArray(career.skills) ? career.skills.join(', ') : '',
        responsibilities: Array.isArray(career.responsibilities) ? career.responsibilities.join(', ') : '',
        location: career.location || 'Bhugaon, Pune',
        isRemote: Boolean(career.isRemote),
        isActive: Boolean(career.isActive)
      });
    }
  }, [career]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ 
      ...prev, 
      [name]: type === 'checkbox' ? checked : value 
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      // Validate required fields
      const requiredFields = ['title', 'category', 'positions', 'background', 'experience', 'salary', 'duration'];
      for (const field of requiredFields) {
        if (!formData[field] || formData[field].toString().trim() === '') {
          throw new Error(`${field.charAt(0).toUpperCase() + field.slice(1)} is required`);
        }
      }

      // Validate skills and responsibilities
      if (!formData.skills.trim()) {
        throw new Error('At least one skill is required');
      }
      if (!formData.responsibilities.trim()) {
        throw new Error('At least one responsibility is required');
      }

      // Prepare request data
      const requestData = {
        title: formData.title.trim(),
        category: formData.category,
        positions: parseInt(formData.positions) || 1,
        background: formData.background.trim(),
        experience: formData.experience.trim(),
        salary: formData.salary.trim(),
        duration: formData.duration.trim(),
        skills: formData.skills.split(',').map(s => s.trim()).filter(s => s),
        responsibilities: formData.responsibilities.split(',').map(r => r.trim()).filter(r => r),
        location: formData.location.trim(),
        isRemote: Boolean(formData.isRemote),
        isActive: Boolean(formData.isActive)
      };

      // Make API request using careerService
      if (career) {
        await careerService.updateCareer(career._id, requestData);
      } else {
        await careerService.createCareer(requestData);
      }
      
      if (onSave) onSave();
      if (onClose) onClose();
      
    } catch (err) {
      setError(err.message || 'Failed to save career posting');
    } finally {
      setIsLoading(false);
    }
  };

  const categoryOptions = [
    { value: 'Entry Level', label: 'Entry Level', icon: Users },
    { value: 'Creative', label: 'Creative', icon: Target },
    { value: 'Operations', label: 'Operations', icon: Briefcase },
    { value: 'Leadership', label: 'Leadership', icon: Award },
    { value: 'Coordination', label: 'Coordination', icon: Globe }
  ];

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <div className="p-4 mb-4 text-red-700 bg-red-100 rounded-lg">
          <div className="font-medium">{error}</div>
        </div>
      )}
      
      <div>
        <label className="block text-sm font-medium text-gray-700">Title *</label>
        <input
          type="text"
          name="title"
          value={formData.title}
          onChange={handleChange}
          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#166534] focus:border-[#166534]"
          required
          placeholder="Job Title"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700">Category *</label>
        <select
          name="category"
          value={formData.category}
          onChange={handleChange}
          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#166534] focus:border-[#166534]"
          required
        >
          {categoryOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700">Positions *</label>
          <input
            type="number"
            name="positions"
            min="1"
            value={formData.positions}
            onChange={handleChange}
            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#166534] focus:border-[#166534]"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Experience *</label>
          <input
            type="text"
            name="experience"
            value={formData.experience}
            onChange={handleChange}
            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#166534] focus:border-[#166534]"
            required
            placeholder="0-2 years"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700">Salary *</label>
          <input
            type="text"
            name="salary"
            value={formData.salary}
            onChange={handleChange}
            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#166534] focus:border-[#166534]"
            required
            placeholder="₹15,000 - 25,000 PM"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Duration *</label>
          <input
            type="text"
            name="duration"
            value={formData.duration}
            onChange={handleChange}
            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#166534] focus:border-[#166534]"
            required
            placeholder="3 months probation"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700">Background *</label>
        <textarea
          name="background"
          value={formData.background}
          onChange={handleChange}
          rows="3"
          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#166534] focus:border-[#166534]"
          required
          placeholder="Educational background or requirements"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700">Skills *</label>
        <textarea
          name="skills"
          value={formData.skills}
          onChange={handleChange}
          rows="3"
          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#166534] focus:border-[#166534]"
          required
          placeholder="Skill 1, Skill 2, Skill 3"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700">Responsibilities *</label>
        <textarea
          name="responsibilities"
          value={formData.responsibilities}
          onChange={handleChange}
          rows="4"
          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#166534] focus:border-[#166534]"
          required
          placeholder="Responsibility 1, Responsibility 2"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700">Location</label>
          <input
            type="text"
            name="location"
            value={formData.location}
            onChange={handleChange}
            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#166534] focus:border-[#166534]"
            placeholder="Bhugaon, Pune"
          />
        </div>

        <div className="flex items-center space-x-4 pt-6">
          <div className="flex items-center">
            <input
              type="checkbox"
              name="isRemote"
              checked={formData.isRemote}
              onChange={handleChange}
              className="h-4 w-4 text-[#166534] focus:ring-[#166534] border-gray-300 rounded"
            />
            <label className="ml-2 block text-sm text-gray-700">Remote Friendly</label>
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              name="isActive"
              checked={formData.isActive}
              onChange={handleChange}
              className="h-4 w-4 text-[#166534] focus:ring-[#166534] border-gray-300 rounded"
            />
            <label className="ml-2 block text-sm text-gray-700">Active Listing</label>
          </div>
        </div>
      </div>

      <div className="flex justify-end space-x-3 pt-4">
        <button
          type="button"
          onClick={onClose}
          className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#166534]"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isLoading}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-[#166534] hover:bg-[#0e3d1f] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#166534] disabled:opacity-50"
        >
          {isLoading ? (
            <>
              <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              {career ? 'Updating...' : 'Creating...'}
            </>
          ) : career ? 'Update Career' : 'Create Career'}
        </button>
      </div>
    </form>
  );
};

export default CareerForm;